/**
 * Static FAQ content for the Help Center page and the support chatbot's cached system prompt.
 *
 * IMPORTANT: When updating, keep this file in sync with:
 *   AI_Calling_For_Projects/new_chat_faq_to_add.md (Section 2)
 * Both are source-of-truth together — the markdown is for the bot's prompt,
 * this file is for the page display.
 */

export interface FAQEntry {
  q: string;
  a: string;
}

export interface FAQSection {
  id: string;
  title: string;
  entries: FAQEntry[];
}

export const FAQ_SECTIONS: FAQSection[] = [
  {
    id: 'account-login',
    title: 'Account, Login & Email Verification',
    entries: [
      {
        q: 'How do I log in?',
        a: "Go to portal.investorcalls.ai. Enter the email and password from your welcome email. If you're a first-time user, you'll need to click the \"Verify Email Address\" link in your welcome email first.",
      },
      {
        q: 'How do I reset my password?',
        a: "Click \"Forgot Password?\" on the login page, enter your email, and click \"Send Reset Link.\" You'll receive an email with a reset link. Check Spam/Junk if you don't see it within a few minutes.",
      },
      {
        q: "I didn't receive the email verification link — what do I do?",
        a: 'Check your Spam/Junk folder. Search for "JAD Enterprises" or "info@jadenterprises.org". If you still don\'t see it, click "Resend Verification Email" on the portal. If none of those work, email info@jadenterprises.org.',
      },
      {
        q: "I verified my email but still can't log in.",
        a: 'Return to the portal and click "I\'ve Verified My Email." If that doesn\'t work, try refreshing the page or logging in a fresh browser. If problems persist, use the chat assistant and it will check your account status directly.',
      },
      {
        q: 'What happens if my platform fee expires?',
        a: "You won't be able to send new calls until you renew. Credits never expire, but you need an active monthly platform fee to use them. Your leads and call results stay in the portal while your account is active. After 180 days with no activity, we may delete them. You can renew any time by clicking \"Buy Credits\" and selecting \"Platform Fee.\"",
      },
      {
        q: 'How long do you keep my data if I stop paying?',
        a: 'Your leads and call results stay in the portal while your account is active. After 180 days with no activity, we may delete them. Credits never expire, but you need an active monthly platform fee to use them.',
      },
    ],
  },
  {
    id: 'credits-billing',
    title: 'Credits & Billing',
    entries: [
      {
        q: "What's the difference between Per-Call and Per-Contact billing?",
        a: 'Per-Call: the list price is $0.13–$0.15 per call attempt, regardless of outcome. Best for high-volume campaigns with predictable cost. Per-Contact: the list price is $3.20–$3.50 per qualified contact — only when the AI reaches the right person AND the conversation lasts 30+ seconds. Best when you want to pay only for real conversations. Your billing type is set when your account is created. To switch, email info@jadenterprises.org.',
      },
      {
        q: 'How much does the platform fee cost, and what does it cover?',
        a: 'The list price is $500/month. If your account has a discount, the Buy Credits screen shows the price you pay. It covers your dedicated portal, AI agent hosting, phone number, call recording, result classification, real-time Google Sheet sync, and support. First-time payers also receive a 2,500 free call credit welcome bonus.',
      },
      {
        q: 'How do I buy credits?',
        a: 'Click the gold "Buy Credits" button in the top-right of your dashboard. Select a package or the Platform Fee and review the price. If you have a promo code, type it into the "Have a promo code?" box on that review step and press Apply — the new price shows before you pay. Then enter your password to confirm and complete checkout on Stripe. Your credits land in your account within seconds of payment; a receipt is emailed automatically.',
      },
      {
        q: 'Are credits refundable?',
        a: 'No. Credits are not refundable. Unused credits stay on your account, and you need an active monthly platform fee to use them. Questions? Email info@jadenterprises.org.',
      },
      {
        q: 'Credits were deducted but no calls went out — why?',
        a: "Credits are held when you click Send. If a call fails to go through for a system reason, the credit is automatically returned to your balance. Check your balance after 2–5 minutes. If it doesn't return, use the chat assistant — it will look at your account directly.",
      },
      {
        q: "My receipt didn't arrive in my email.",
        a: "Check Spam/Junk for mail from info@jadenterprises.org. Receipts usually arrive within a minute of successful payment. If it's been more than 30 minutes and nothing is there, use the chat assistant or contact support.",
      },
      {
        q: 'Do credits expire?',
        a: 'No. Credits never expire, but you need an active monthly platform fee to use them.',
      },
      {
        q: "What's the 2,500 free credit bonus?",
        a: 'New accounts that pay their first platform fee receive 2,500 free call credits as a welcome bonus. It is automatic — the welcome bonus does not need a promo code.',
      },
      {
        q: 'I have a promo code — how do I use it?',
        a: 'Click "Buy Credits", pick what you want to buy, and on the review step type the code into the "Have a promo code?" box, then press Apply. The discounted price shows right away, before you pay. Promo codes are entered in the portal only — there is no code box on the Stripe payment page. You always receive the full number of credits; a code only lowers the price.',
      },
      {
        q: 'My promo code was not accepted, or my discount did not show.',
        a: 'A code can be refused because it has expired, is not active yet, was already used, applies to something else (credit packs vs. the platform fee), or is not valid for your account. If your account already has a discount that is as good or better, that discount is used and your code is kept for later — a code and an account discount never combine. Nothing is charged when a code is refused. After several wrong tries the code box locks for an hour. Still stuck? Use the chat assistant or email info@jadenterprises.org.',
      },
    ],
  },
  {
    id: 'uploading-leads',
    title: 'Uploading Leads',
    entries: [
      {
        q: 'What file formats can I upload?',
        a: 'CSV (.csv), Excel (.xls / .xlsx), and plain text (.txt). No conversion needed — upload directly. The system auto-detects column headers from 170+ common variations.',
      },
      {
        q: 'What columns does my file need?',
        a: 'Required: a phone number column. Required: EITHER separate first and last name columns, OR a single full-name column. Optional: email, address, city, state, zip. Up to 3 phone columns are supported — the best phone is auto-selected.',
      },
      {
        q: 'Why were some of my leads filtered out?',
        a: 'The system removes invalid phone numbers, known-disconnected phones, toll-free lines, duplicates already on your account, business names (LLC, Corp, Trust), placeholder entries (N/A, Unknown, Test), and entries that don\'t match real person names (validated against a 1.8M+ name database). This filtering is free and automatic — you only pay for callable contacts.',
      },
      {
        q: 'What is the "Cell phones only" toggle?',
        a: 'A switch on the upload screen that excludes all landlines and VoIP numbers. When on, only confirmed cell phones are kept. Useful for SMS follow-up campaigns or mobile-only targeting.',
      },
      {
        q: 'Is there a maximum file size I can upload?',
        a: 'No practical cap. Files of any size go through one Stripe checkout, one charge, and one receipt — large uploads are split into background batches automatically. A 5,000-row file is ready in about 3 seconds; a 50,000-row file in about 10 seconds; a 1,000,000-row file in roughly 3 minutes before the checkout opens. You can keep using the rest of the portal while big uploads finish.',
      },
      {
        q: 'How do I delete leads I already uploaded?',
        a: 'Click the red "Delete Leads" button below your Run grid. Choose "Delete All Pending Leads" or a specific Run. Type DELETE in capital letters to confirm. Only un-called pending leads are deleted — results, in-progress calls, and historical data are preserved.',
      },
      {
        q: 'I sent too many calls — can I stop them?',
        a: 'Not once they are sent. Calls start within a minute of pressing Send, and a run of 2,000 usually finishes in 15–20 minutes, so a send cannot be paused or pulled back, and credits for calls that went out are used. What you can control: (1) leads you have not sent yet — remove them with the red "Delete Leads" button; (2) the next send — type the number of calls you want in the box under the Run button before you press it. Calls only go out when you press Send, when a schedule you created fires, or through automatic sending, which is off unless we turn it on for you.',
      },
      {
        q: 'Why were names filtered out of my upload?',
        a: 'Names are validated against a 1.8M+ real-person-name database. Entries that look like business names, placeholders ("N/A", "Unknown", "Test"), or junk entries are filtered out so you only call real people. Your upload summary shows how many were removed and why.',
      },
    ],
  },
  {
    id: 'verification',
    title: 'Phone Verification & Data Cleaning',
    entries: [
      {
        q: 'What is phone verification?',
        a: 'An optional paid step on upload. Phones we can\'t confirm from our 12M+ bad / 4M+ good cached records go through a verification service that (1) tests each line to confirm it\'s still active ("Disconnect Check"), and optionally (2) looks up the registered name on the number to confirm it matches your list ("Reverse Phone Check").',
      },
      {
        q: 'How much does phone verification cost?',
        a: 'Per-Call clients: $0.004/phone for disconnect-only, $0.025/phone for disconnect + reverse. Per-Contact clients: verification is required; disconnect + reverse is $0.029/phone combined. You see the exact cost on the upload preview before paying.',
      },
      {
        q: 'How long does phone verification take?',
        a: 'Typically 10–30 minutes depending on batch size. You\'ll see "Phone Disconnect in process" or "Reverse Phone in process" on your dashboard while it runs, and a pop-up notification when it completes.',
      },
      {
        q: 'What is the Deferred Verification Queue?',
        a: 'When you upload leads and skip immediate verification, unconfirmed phones are saved to the Deferred Verification Queue on your dashboard. You can verify them later in batches of 5,000, 10,000, 25,000, or Run All. Minimum batch size is 5,000 — this keeps per-phone cost low.',
      },
      {
        q: 'What does "Phone Disconnect in process" mean?',
        a: 'Your leads are being verified right now. Each phone is being tested to confirm it\'s active and in service. You can keep using the rest of the portal while it runs. A notification will pop up when it\'s done.',
      },
      {
        q: "What's the difference between Disconnect Check and Reverse Phone Check?",
        a: 'Disconnect Check confirms the phone line is still active and in service. Reverse Phone Check looks up the name registered to that phone number and confirms it matches the name on your list. Catches cases where the phone is active but belongs to someone else now.',
      },
      {
        q: 'Why are per-contact clients required to do reverse phone verification?',
        a: 'Per-contact billing charges only when the AI reaches the right person for 30+ seconds. Reverse phone verification is what allows the system to know the right person was reached — without it, we couldn\'t bill accurately.',
      },
      {
        q: 'What is the nightly phone scrub?',
        a: "Every night, the system re-checks the phones in your pending queue against updated disconnect data. If any newly-disconnected numbers are found, they're automatically removed. You'll see a notification on your dashboard listing how many were removed. Free — no charge.",
      },
    ],
  },
  {
    id: 'sending-calls',
    title: 'Sending Calls',
    entries: [
      {
        q: 'What are the calling hours?',
        a: 'You set your window in Call Settings, in Central Time. New accounts start at 9:00 AM – 5:00 PM, and you can set it anywhere from 8:00 AM to 7:00 PM. Each lead is called only when its own local time is inside your window. Weekend calls are possible. Most clients don\'t use them.',
      },
      {
        q: "What's the minimum and maximum per send?",
        a: 'Minimum: 100 calls per batch. Maximum: 2,000 calls per batch by default — the team can dial individual accounts up to 5,000 if you need bigger batches. Daily limit: 10,000 calls per day. Exception: if you have fewer than 100 call credits (on per-call billing) or fewer than 100 eligible leads in a run, you can send the smaller amount.',
      },
      {
        q: "What's the monthly limit?",
        a: 'Per-Call clients: 30,000 calls per month by default. Per-Contact clients: 1,200 contacts per month by default. Limits reset on the 1st of each month. If you need more, click "Request Higher Limit" on your dashboard.',
      },
      {
        q: 'Why is there a cooldown timer?',
        a: 'After each batch of calls, a short cooldown lets the system process results before the next batch. It is 15 minutes per 200 calls sent for per-call clients and 10 minutes per 200 calls sent for per-contact clients. The timer on your dashboard shows the time left.',
      },
      {
        q: 'What does "No eligible leads" mean for a Run?',
        a: "Either all leads in that Run have already been called, or they're in the recall-waiting period. Upload new leads, wait for recall, or try a different Run.",
      },
      {
        q: 'How do I test my agent before sending calls?',
        a: 'Click "Test My Agent" on the dashboard. Enter your phone and first name, and the agent will call you immediately so you can experience the conversation. Free: you get 5 test calls, and they refill once a day. No credits deducted.',
      },
      {
        q: "What's the Run Grid?",
        a: 'Eight numbered buttons (Run 1–8) representing call attempts. New leads start at Run 1. If a lead doesn\'t connect, it moves to Run 2 after the recall period, and so on up to Run 8. After 8 attempts with no disposition, the lead is marked as a "Dead Lead" and permanently removed.',
      },
      {
        q: 'How do I request a higher monthly limit?',
        a: 'When you\'re near your limit a "Request Higher Limit" button appears on your dashboard. Click it, fill out name / phone / email / requested volume / notes, and submit. The request emails our team and we usually respond within 1 business day.',
      },
      {
        q: 'Can I send calls only from a specific upload file?',
        a: 'Yes. On the Send Run dialog, switch the "Lead source" radio from "Send all eligible leads" to "Send by file." Pick up to 30 files. Each row shows "X ready / Y in Run N" so you know what is available right now versus still in recall. Only the files you pick are called — if they don\'t have enough eligible leads, fewer calls go out and the system will not top up from other files.',
      },
      {
        q: 'Can I schedule recurring or one-time call runs?',
        a: 'Yes. Open "Schedule Calls" from the dashboard. Pick a Run number, count (100 – 2,000 leads — scheduled runs cap lower than manual sends because nobody is watching them in real time), days/times in the 8 AM – 7 PM CST window, and either weekly or one-time. You can optionally scope the schedule to specific files (same picker as the dashboard). Up to 3 schedules per account per day; back-to-back fires must be at least 15 minutes per 200 leads apart so the system has time to process the first batch. One-time schedules disappear from the list once they fire (the record is preserved on the back end for audit).',
      },
      {
        q: 'Why was my new schedule rejected with a "conflicts with another schedule" message?',
        a: 'Two schedules on the same day must be far enough apart that the first one\'s cooldown clears before the second fires. The required gap is 15 minutes per 200 leads in the earlier fire — so a 1,000-lead schedule needs 75 minutes after it before the next one can run. Move one of the times, lower the count on the earlier fire, or split across two days.',
      },
    ],
  },
  {
    id: 'inbound-calls',
    title: 'Inbound Calls (Callbacks)',
    entries: [
      {
        q: 'Can my agent answer when a prospect calls back?',
        a: 'Yes, when your account is set up for inbound calling. If a prospect calls one of your numbers within 72 hours of an outbound attempt, the agent recognizes them, greets them by name, and continues the conversation. Inbound calling is opt-in: a "Receive Inbound Calls" section appears on your dashboard once the team assigns you an inbound agent.',
      },
      {
        q: 'How is inbound calling billed?',
        a: 'On per-call plans, callbacks are free — no credit is used. On per-contact plans, one contact credit is deducted per qualifying inbound call. To qualify, the call must (1) last at least 20 seconds, (2) come within 72 hours of the most recent outbound attempt to that lead, and (3) be the first credited callback for the current Run. Calls that don\'t meet all three rules are not charged — they still show up in your results.',
      },
      {
        q: 'How do I turn inbound calling on or off?',
        a: 'On the dashboard, find the "Receive Inbound Calls" toggle. Turning it on shows a confirmation explaining the credit rule. Turning it off stops new callbacks from routing to your agent immediately. Calls already in progress finish normally.',
      },
      {
        q: 'My dashboard says "Disabled by administrator" for inbound — what does that mean?',
        a: 'The team has temporarily turned inbound calling off for your account (account review, configuration change, or a billing question). The toggle is locked until the team flips it back. Email info@jadenterprises.org or use the chat assistant to check status.',
      },
    ],
  },
  {
    id: 'outcomes',
    title: 'Call Outcomes & Classifications',
    entries: [
      {
        q: 'What do the five color-coded result cards mean?',
        a: 'Good Leads (green): Reached, 60+ seconds, interested, positive signals — follow up personally ASAP. Good For Later (blue): Reached, 30+ seconds, interested, wants a callback later. Not Interested (orange): Reached, 30+ seconds, clearly declined. Bad Leads (red): Wrong number, disconnected, fax line, gatekeeper, Do Not Call, or no money. Non Accredited (purple): Confirmed not accredited (only applies to accredited-only campaigns). Your Google Sheet also has a Dead Leads tab for leads called 8 times with no disposition. Once a lead is marked Good Leads or Good For Later, it is permanently protected — the nightly cleanup will never silently remove it from your queue, even if the same number later shows up on a bad list from another source.',
      },
      {
        q: 'When is a call counted as a "contact" for per-contact billing?',
        a: 'When the AI reaches the right person (verified via reverse phone check) AND the conversation lasts 30+ seconds. Shorter calls and wrong-number calls are not charged.',
      },
      {
        q: 'What does the AI do if someone asks it to stop calling?',
        a: 'Do Not Call requests are honored immediately — the agent exits the call and permanently flags that number. The lead is classified as a Bad Lead and will never be called again across any Run.',
      },
      {
        q: 'What does the AI do with voicemails?',
        a: 'The agent detects voicemail and ends the call silently (no message left). The lead is marked "no answer" and moves to the next Run after your recall waiting period.',
      },
      {
        q: "Will the AI admit it's an AI?",
        a: 'If someone directly asks whether they\'re speaking to a real person or a machine, yes — the agent honestly identifies itself as an AI assistant. It never claims to be human when asked.',
      },
      {
        q: 'How long can my AI call someone?',
        a: 'Typical call is 2–5 minutes. Hard cap is 6 minutes. The pacing is natural — not rushed.',
      },
      {
        q: 'What happens after 8 Runs with no disposition?',
        a: 'The lead is classified as a "Dead Lead" and permanently removed from your calling queue.',
      },
    ],
  },
  {
    id: 'runs-recall',
    title: 'Runs & Recall System',
    entries: [
      {
        q: 'How does the Run system work?',
        a: 'Every new lead starts at Run 1. If the AI doesn\'t reach them on the first attempt (no answer, voicemail, wrong time), the lead moves to Run 2 after your recall waiting period — and so on up to Run 8. Reached-but-not-ready leads also go to Run 2 if they asked for a callback.',
      },
      {
        q: "What's the recall waiting period?",
        a: 'Two adjustable settings in Call Settings: No answer/voicemail: 5 days between attempts (default). Answered but not ready: 30 days between attempts (default). You can change both in Call Settings.',
      },
      {
        q: "Why can't I call my Run 2 leads yet?",
        a: "Because your recall waiting period hasn't elapsed for those leads. They become available automatically once the wait is up. The Run button shows two numbers: the top line is leads ready right now, and a sub-line like \"+300 in recall · ready in 3 days\" tells you how many are still waiting and the soonest they'll unlock. If you want to shorten the wait, go to Call Settings. The chat assistant can also look at your specific Run 2 leads and tell you exactly when they'll be available.",
      },
      {
        q: 'Are DNC and Bad Leads ever recalled?',
        a: 'No. DNC requests and Bad Lead classifications permanently remove the lead from every Run. They never come back.',
      },
    ],
  },
  {
    id: 'results-exports',
    title: 'Results, Exports & Google Sheet',
    entries: [
      {
        q: 'How quickly do I see call results?',
        a: 'In real-time. As each call completes, the result appears on your dashboard and in your connected Google Sheet automatically. No need to refresh.',
      },
      {
        q: 'How do I export my results?',
        a: 'Click "Export Good Leads" or "Export All Calls" on your dashboard. The file is emailed to your account email as an Excel attachment (.xlsx) with 20 columns of data per result. Works for any size — even 50,000+ rows.',
      },
      {
        q: "Where's the Google Sheet?",
        a: 'Every client gets a connected Google Sheet that updates in real-time. The link is on your dashboard. Separate tabs for Good Leads, Good For Later, Not Interested, Bad Leads, Non Accredited, and Dead Leads.',
      },
      {
        q: 'How long are call recordings available?',
        a: '90 days from the date of the call. Click the "Listen" link in any result to hear the recording.',
      },
      {
        q: "My export didn't arrive — where is it?",
        a: "Check Spam/Junk for email from info@jadenterprises.org. Large exports (50,000+ rows) can take a few minutes to generate. If it's been more than 30 minutes, use the chat assistant and it will check your account.",
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting (Quick Reference)',
    entries: [
      {
        q: "I can't log in.",
        a: 'Try "Forgot Password?" → reset via email. Confirm your email is verified. If neither works, use the chat assistant and it will check your account.',
      },
      {
        q: 'Buttons on my dashboard are greyed out.',
        a: 'Check your credit balance (0 credits disables sending). Check the cooldown timer at the top of the Run grid.',
      },
      {
        q: 'My upload failed.',
        a: 'Confirm your file has both a phone column and a name column. Try CSV if Excel failed. The chat assistant can look at your last upload for a full breakdown.',
      },
      {
        q: '"Daily cap reached" error.',
        a: "You've sent 10,000 calls today. The limit resets at midnight CST. Try again tomorrow.",
      },
      {
        q: '"No eligible leads" message.',
        a: 'All leads in that Run have already been called or are waiting for the recall period. Upload new leads or wait.',
      },
      {
        q: "I don't see my results yet.",
        a: 'Results appear in real-time as calls finish. If you just sent a batch, give it a few minutes — each call takes 2 to 6 minutes.',
      },
      {
        q: "My export didn't arrive.",
        a: 'Check your Spam/Junk folder. Large exports may take a few minutes to generate.',
      },
      {
        q: 'Page is blank or not loading.',
        a: 'Try refreshing the page (F5 or Ctrl+R). Clear your browser cache. Try a different browser (Chrome recommended).',
      },
      {
        q: '"Platform fee expired" message.',
        a: 'Your monthly platform fee needs to be renewed. Click "Buy Credits" and select the Platform Fee option.',
      },
    ],
  },
  {
    id: 'policies',
    title: 'Policies, Contract & Compliance',
    entries: [
      {
        q: "What's the Service Agreement?",
        a: 'The legal contract you sign on your first purchase. Covers billing terms, credits, DNC obligations, and data use. View at investorcalls.ai/terms-of-service.',
      },
      {
        q: 'Do I need to scrub my lead list against the National Do Not Call Registry?',
        a: "Yes. You're responsible for scrubbing your lists before upload. The system also honors DNC requests made during calls — immediately and permanently.",
      },
      {
        q: 'Where is my data stored, and who can see it?',
        a: 'Your data is isolated from other clients — no one else can see your leads, results, or account. Storage is on enterprise-grade cloud infrastructure with encryption. Only you and authorized JAD Enterprises admin staff have access.',
      },
      {
        q: 'Are my payments secure?',
        a: 'Yes. All payments are processed by Stripe (PCI-compliant). Your card number never touches our servers. Receipts are emailed for every transaction.',
      },
      {
        q: 'Can I cancel?',
        a: 'Yes. Written 30-day notice either side. Credits are not refundable. The $2,500 agent creation fee is non-refundable once development begins.',
      },
    ],
  },
  {
    id: 'help-contact',
    title: 'Help & Contact',
    entries: [
      {
        q: 'How do I contact support directly?',
        a: 'Email: info@jadenterprises.org (response within 1 business day). Phone: (214) 304-2168. Chat: Use the chat assistant on this Help page — it can look at your actual account and help diagnose issues instantly.',
      },
    ],
  },
];
